import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type TransitionEvent as ReactTransitionEvent } from "react";
import { emptyHomeDashboardState } from "../../application/home-dashboard";
import { shiftISODate } from "./day-labels";

export const DAY_CARD_GAP = 20;

export function useDayNavigation({ selectedDate, currentDate, previousDashboard, nextDashboard, setDashboard, setSelectedDate }: Pick<ReturnType<typeof import("../home/useDashboard").useDashboard>, "selectedDate" | "currentDate" | "previousDashboard" | "nextDashboard" | "setDashboard" | "setSelectedDate">) {
  const daySwipeRef = useRef<{ pointerId: number; x: number; y: number; width: number; horizontal: boolean | null } | null>(null);
  const suppressDayClickRef = useRef(false);
  const pendingDayRef = useRef<{ date: string; days: number; width: number } | null>(null);
  const [daySlideOffset, setDaySlideOffset] = useState(0);
  const [daySlidePhase, setDaySlidePhase] = useState<"idle" | "dragging" | "leaving">("idle");
  const [animateTodayRing, setAnimateTodayRing] = useState(true);
  useEffect(() => {
    const timer = window.setTimeout(() => setAnimateTodayRing(false), 1_200);
    return () => window.clearTimeout(timer);
  }, []);

  const changeDay = (days: number) => {
    if (daySlidePhase !== "idle") return;
    const next = shiftISODate(selectedDate, days);
    if (next > currentDate) return;
    const width = document.querySelector<HTMLElement>(".today")?.clientWidth ?? window.innerWidth;
    pendingDayRef.current = { date: next, days, width };
    setDaySlidePhase("leaving");
    setDaySlideOffset(days < 0 ? width + DAY_CARD_GAP : -(width + DAY_CARD_GAP));
  };
  const startDaySwipe = (event: ReactPointerEvent<HTMLElement>) => {
    if (daySlidePhase !== "idle") return;
    daySwipeRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, width: event.currentTarget.clientWidth, horizontal: null };
  };
  const moveDaySwipe = (event: ReactPointerEvent<HTMLElement>) => {
    const start = daySwipeRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (start.horizontal === null && (Math.abs(dx) > 7 || Math.abs(dy) > 7)) start.horizontal = Math.abs(dx) > Math.abs(dy);
    if (!start.horizontal) return;
    event.preventDefault();
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.setPointerCapture(event.pointerId);
    suppressDayClickRef.current = true;
    setDaySlidePhase("dragging");
    setDaySlideOffset(dx < 0 && selectedDate >= currentDate ? dx * 0.16 : dx);
  };
  const finishDaySwipe = (event: ReactPointerEvent<HTMLElement>) => {
    const start = daySwipeRef.current;
    daySwipeRef.current = null;
    if (!start || start.pointerId !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    const days = dx > 0 ? -1 : 1;
    const next = shiftISODate(selectedDate, days);
    const shouldChange = start.horizontal === true && Math.abs(dx) >= start.width * .15 && Math.abs(dx) > Math.abs(dy) && next <= currentDate;
    if (shouldChange) {
      pendingDayRef.current = { date: next, days, width: start.width };
      setDaySlidePhase("leaving");
      setDaySlideOffset(dx > 0 ? start.width + DAY_CARD_GAP : -(start.width + DAY_CARD_GAP));
    } else {
      setDaySlidePhase("idle");
      setDaySlideOffset(0);
    }
    window.setTimeout(() => { suppressDayClickRef.current = false; }, 250);
  };
  const finishDaySlide = (event: ReactTransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || event.propertyName !== "transform") return;
    const pending = pendingDayRef.current;
    if (daySlidePhase === "leaving" && pending) {
      setDashboard(pending.days < 0 ? previousDashboard : nextDashboard ?? emptyHomeDashboardState(pending.date));
      setSelectedDate(pending.date);
      setDaySlidePhase("dragging");
      setDaySlideOffset(0);
      requestAnimationFrame(() => requestAnimationFrame(() => setDaySlidePhase("idle")));
      pendingDayRef.current = null;
    }
  };

  const cancelDaySwipe = () => { daySwipeRef.current = null; setDaySlidePhase("idle"); setDaySlideOffset(0); };
  const suppressDayClick = (event: import("react").MouseEvent<HTMLElement>) => { if (suppressDayClickRef.current) { event.preventDefault(); event.stopPropagation(); } };

  return { daySlideOffset, daySlidePhase, animateTodayRing, changeDay, startDaySwipe, moveDaySwipe, finishDaySwipe, finishDaySlide, cancelDaySwipe, suppressDayClick };
}
